// Picks up a newly deployed client. A tab or installed app can sit suspended for days and resume on an old build, so
// whenever the page comes back to the foreground it re-reads index.html (never from a cache) and compares its app.js
// key (the build's hash, see web/tools/client-cache.mjs) with the running script's. A new build reloads the page while
// the player is outside a world; inside one it waits until they leave.
import { api } from './api';

const MIN_INTERVAL_MS = 60_000;
let pending = false;
let checking = false;
let lastCheck = 0;

/** The running bundle's key, or null for an unversioned build (tests, custom bundles), which never checks. */
function runningKey(): string | null {
  try { return new URL(import.meta.url).searchParams.get('v'); } catch { return null; }
}

function inWorld(): boolean {
  return (api.sessionState() as { state: string }).state !== 'none';
}

/** Reloads into a waiting new build if the player isn't in a world; true when the page is reloading. */
export function reloadForUpdate(): boolean {
  if (!pending || inWorld()) return false;
  console.info('Loading the new client version.');
  location.reload();
  return true;
}

async function check(): Promise<void> {
  const running = runningKey();
  if (!running || pending || checking || performance.now() - lastCheck < MIN_INTERVAL_MS) return;
  checking = true;
  lastCheck = performance.now();
  try {
    const page = await (await fetch(new URL('index.html', location.href), { cache: 'no-store' })).text();
    const deployed = /src="app\.js\?v=([0-9a-f]+)"/.exec(page)?.[1];
    if (deployed && deployed !== running) {
      pending = true;
      console.info(`A new client version is available (${deployed}).`);
      reloadForUpdate();
    }
  } catch (e) {
    console.warn('Could not check for a new client version:', e);
  } finally {
    checking = false;
  }
}

export function installClientUpdates(): void {
  lastCheck = performance.now(); // boot just loaded the current page
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void check(); });
  window.addEventListener('pageshow', e => { if (e.persisted) void check(); }); // restored from the back/forward cache
}
